import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Popup } from "@/Common/Components/Modals/Popup";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { FaxHelper } from "@/Common/Helpers/FaxHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EnumSentFaxStatus } from "@/Common/Models/Faxing/EnumSentFaxStatus";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { PortfolioInboxes, PortfolioSentFaxSortOrderType, RedirectTo } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { LinkButton } from "@/DesignSystem";
import { FaxAndSignatureClinicianSelectionState } from "@/ExportableFile/Components/FaxAndSignatureClinicianSelection";
import { IFaxCoverSheetProps } from "@/ExportableFile/Components/IFaxInformationProps";
import { PortfolioFaxSettingsContainer } from "@/Portfolio/Components/PortfolioFaxSettingsContainer";
import { SentFaxInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { MySentFaxTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { ISentFaxInboxModel } from "@/Portfolio/Models/UI/ISentFaxInboxModel";
import { PortfolioFaxInboxStatusField } from "./PortfolioFaxInboxStatusField";
import { PortfolioResendFaxPopup } from "./PortfolioResendFaxPopup";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";

interface IPortfolioFaxInboxProps {
    model: ISentFaxInboxModel;
    previewFileUrl?: string;
    title: string;
    inboxType: PortfolioInboxes;
}

class PortfolioFaxInboxState {
    public currentItemId: string;
    public showPreview: boolean;
    public previewFileContent: string;

    public sentFaxItem: SentFaxInboxItem;
    public previewPDF: string;
    public isValidPin: boolean;
    public hidePinValidation: boolean;
    public withSignature: boolean;
    public isPermanentFaxMessagePath: boolean;
    public currentEmployeeId: string;
    public patientClinicianModel: IPatientCliniciansModel;

    public clinicianInfoState: FaxAndSignatureClinicianSelectionState;
    public isSendFaxValid: boolean;
    public isSelectAllChecked: boolean;
    public allFaxes: MySentFaxTableItem[];
    public sendingFax: boolean;
    public isResendFaxOpen: boolean;

    public static getDefault(props: IPortfolioFaxInboxProps): PortfolioFaxInboxState {
        const state = new PortfolioFaxInboxState();
        state.currentItemId = "";
        state.showPreview = false;
        state.previewFileContent = "";
        state.sentFaxItem = null;
        state.previewPDF = "";
        state.isValidPin = false;
        state.hidePinValidation = false;
        state.withSignature = false;
        state.isPermanentFaxMessagePath = false;
        state.currentEmployeeId = sessionStore.getCurrentEmployee().ContactFacetId;

        state.patientClinicianModel = null;
        state.clinicianInfoState = null;
        state.isSendFaxValid = false;
        state.isSelectAllChecked = false;
        state.allFaxes = props.model.get("TableData").toArray();
        state.sendingFax = false;
        state.isResendFaxOpen = false;
        return state;
    }
}

class PortfolioFaxInbox extends BaseReactComponent<IPortfolioFaxInboxProps, PortfolioFaxInboxState> {
    private helper: MyleHelper = new MyleHelper();
    public constructor(props: IPortfolioFaxInboxProps) {
        super(props);

        this.state = PortfolioFaxInboxState.getDefault(props);
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: IPortfolioFaxInboxProps): void {
        if (this.props !== prevProps) {
            this.mutateState(s => {
                if (this.props.model.get("TableData") != prevProps.model.get("TableData")) {
                    s.allFaxes = this.props.model.get("TableData").toArray();
                    s.isSelectAllChecked = false;
                }
                s.currentItemId = "";
                let selectedSentFaxInboxItem = this.props.model.get("SelectedSentFaxInboxItem");
                if (selectedSentFaxInboxItem) {
                    s.currentItemId = selectedSentFaxInboxItem.ItemId;
                    s.previewPDF = this.props.model.get("SentFaxPDF");
                    s.isPermanentFaxMessagePath = this.props.model.get("IsPermanentFaxMessagePath");
                }
            });
        }
    }

    public render(): JSX.Element {
        const saveOptionKey = PortfolioSaveOptionKey.Fax;
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.GetMyleTableColumnsDefaultOptions());

        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        const cfgm: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        const numberOfSelectedFax = this.state.allFaxes.filter(x => x.IsSelected && (cacheStore.ClinicSettings.EnableResendFaxForAllStatuses || FaxHelper.isFailedFax(x.Model.SentStatus)));

        return (
            <div className="workspace">
                {this.state.showPreview &&
                    this.displayPreviewFax()
                }
                <CommonTableHeaderComponent
                    title={cacheStore.GetString(this.props.inboxType == PortfolioInboxes.Faxes ? "MySentFaxLabel" : "FailedFaxesLabel")}
                    searchText={this.props.model.get("GlobalPortfolioSearchText") || ""}
                    searchPlaceholder={this.props.model.get("GlobalPortfolioSearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchBoxTextChanged)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ShowOptionsButtonClick)}
                />
                <div id="posref" className="ws-scrollable">
                    {this.props.model.get("ShowMySentFaxDisplaySettings") &&
                        <PortfolioFaxSettingsContainer className="filter-component filter-component-responsive" model={this.props.model} currentInbox={this.props.inboxType} />
                    }
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="SentFaxInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.state.allFaxes.toImmutable()}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickTarget={null}
                            RowClickOperation={null}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_PortfolioMySentFaxes_Search_ColumnSortClick}
                            RowClickFunction={item => this.onPreviewFaxGrid(item)}
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.model.get("TotalQueryRows")}
                                CurrentPage={this.props.model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.MySentFaxes_Search_CurrentPageChange}
                            />
                        </div>
                        {!!cacheStore.faxAccounts?.any() && (
                            <div className="col-lg-2 col-md-3 col-sm-4 text-right">
                                <button
                                    disabled={numberOfSelectedFax && numberOfSelectedFax.length <= 0 && !this.state.sendingFax}
                                    className="btn btn-primary push-right-5"
                                    onClick={() => this.onResendMultipleFaxClick()}
                                >
                                    {cacheStore.GetString("ResendLabel")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {this.props.model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Portfolio.MySentFaxes_SearchSettings_ColumnOrderingPopup_Close}
                    />
                )}
                {this.state.isResendFaxOpen && (
                    <PortfolioResendFaxPopup
                        containsCoverSheet={this.state.sentFaxItem.HasCoverPage}
                        initialFaxNumber={"+" + this.state.sentFaxItem.RecipientFax}
                        initialRecipientName={this.state.sentFaxItem.RecipientName}
                        onCancel={() => this.mutateState(s => { s.isResendFaxOpen = false; s.sentFaxItem = null; })}
                        onSubmit={async (faxDetails, then) => {
                            await this.resendFaxToUpdatedNumber(
                                this.state.sentFaxItem.SentFaxId,
                                faxDetails.faxNumber,
                                faxDetails.displayName,
                                faxDetails.phoneNumber,
                                faxDetails.pdfCoverPageParams?.GenerateCoverPage ? faxDetails.pdfCoverPageParams : null
                            );
                            this.mutateState(s => {
                                s.isResendFaxOpen = false;
                                s.sentFaxItem = null;
                            }, then);
                        }}
                    />
                )}
            </div>
        );
    }

    public displayPreviewFax(): JSX.Element {
        const previewRoute = FaxHelper.getPdfPreviewRoute(
            this.state.sentFaxItem.SentFaxId,
            this.state.previewPDF,
            this.state.isPermanentFaxMessagePath,
            this.state.withSignature
        );

        const previewUrl = previewRoute
            ? apiSettings.buildAppUrl(previewRoute, { auth: true })
            : null;

        const requiresPIN = FaxHelper.isPinRequired(this.state.sentFaxItem.ItemType);
        const isFailedFax = FaxHelper.isFailedFax(this.state.sentFaxItem.SentStatus);
        const isEnableResendFaxForAllStatuses = cacheStore.ClinicSettings?.EnableResendFaxForAllStatuses;
        const showResendButtonInPreview = isFailedFax || isEnableResendFaxForAllStatuses;

        return (
            <div>
                {showResendButtonInPreview && (
                    <Popup
                        manageOverlay={true}
                        cssClass="preview-fax-iframe-popup"
                        topPosition={60}
                        title=""
                        onCloseDelegate={() => this.onPreviewClose()}
                        draggable={true}
                    >
                        <div className="p-body">
                            <div className="preview-fax-iframe-container">
                                {previewUrl &&
                                    <iframe id="iframe-pdf-document" src={previewUrl} height="705" width="520" />
                                }
                            </div>
                            <div style={{ "display": "inline" }}>
                                <div style={{ "float": "left", "marginTop": "5px" }}>
                                    {(this.state.currentEmployeeId != this.state.sentFaxItem.SenderEmployeeId && requiresPIN) && (
                                        <label style={{ fontStyle: "italic" }}>
                                            {cacheStore.GetString("OriginalSignatureWillBeAddedLabel")}
                                        </label>
                                    )}

                                </div>
                                <div style={{ "float": "right", "display": "inline-flex", "marginBottom": "5px" }}>
                                    <button
                                        disabled={(!this.state.isValidPin && requiresPIN && !isEnableResendFaxForAllStatuses && !isFailedFax) || !cacheStore.faxAccounts?.any()}
                                        className="btn btn-primary"
                                        onClick={() => {
                                            if (isEnableResendFaxForAllStatuses) {
                                                this.openResendFaxPopup();
                                            }
                                            else {
                                                this.resendFax(this.state.sentFaxItem.SentFaxId);
                                            }
                                        }}
                                    >
                                        <span>{cacheStore.GetString("ResendLabel")}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Popup>
                )}
                {!showResendButtonInPreview && (
                    <Popup manageOverlay={true} topPosition={60} title="" onCloseDelegate={() => this.onPreviewClose()} draggable={true}>
                        <div className="p-body">
                            <iframe id="iframe-pdf-document" src={previewUrl} height="705" width="520" />
                        </div>
                    </Popup>
                )}
            </div>
        );
    }

    private openResendFaxPopup(): void {
        this.mutateState(s => {
            s.isResendFaxOpen = true;
            s.showPreview = false;
        });
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    private GetMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: "Checkbox",
                WidthPixels: 38,
                IsFixedInPlace: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "SentFaxPatient",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.Patient,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.Patient
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DocumentTypeLabel"),
                FieldName: "SentFaxItemType",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.ItemType,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.ItemType
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DocumentDateLabel"),
                FieldName: "SentFaxItemDate",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.ItemDate,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.ItemDate
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("RecipientLabel"),
                FieldName: "SentFaxRecipient",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.Recipient,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.Recipient
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DateSentLabel"),
                FieldName: "SentFaxDateSent",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.SentDate,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.SentDate
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ReceivedDateLabel"),
                FieldName: "SentFaxReceivedDate",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.ReceiveDate,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.ReceiveDate
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SentByLabel"),
                FieldName: "SentFaxSentBy",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.SentBy,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.SentBy
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "SentFaxStatus",
                RowClickEnabled: true,
                WidthPercentageDefault: 8,
                IsSortable: true,
                SortColumn: PortfolioSentFaxSortOrderType.Status,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioSentFaxSortOrderType.Status
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionLabel"),
                FieldName: "SentFaxAction",
                RowClickEnabled: false,
                IsSortable: false,
                SortColumn: PortfolioSentFaxSortOrderType.Action,
                WidthPercentageDefault: 8
            })];
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MySentFaxTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Checkbox":
                        return {
                            renderCell: item => {
                                if (cacheStore.ClinicSettings.EnableResendFaxForAllStatuses || FaxHelper.isFailedFax(item.Model.SentStatus)) {
                                    return (
                                        <div>
                                            <input
                                                type="checkbox"
                                                id={item.Model.ItemId}
                                                onChange={() => this.setSelectedFaxes(item)}
                                                checked={item.IsSelected}
                                            />
                                        </div>
                                    );
                                }
                                else
                                    return (<div />);
                            },
                            renderHeader: () => (
                                <div>
                                    <input
                                        type="checkbox"
                                        onChange={() => this.setSelectedToAllResendFax()}
                                        checked={this.state.isSelectAllChecked}
                                    />
                                </div>
                            )
                        };

                    case "SentFaxPatient":
                        return {
                            renderCell: item => (
                                <div>
                                    {item.Model.PatientContactId ? (
                                        <LinkButton
                                            label={item.Model.PatientName}
                                            onClick={e => {
                                                e.stopPropagation();
                                                this.onPatientClick(item.Model.PatientContactId);
                                            }}
                                        />
                                    ) : item.Model.PatientName
                                    }
                                </div>
                            )
                        };

                    case "SentFaxItemType":
                        return {
                            renderCell: item => (
                                <div>{item.Model.ItemTypeName}</div>
                            )
                        };

                    case "SentFaxItemDate":
                        return {
                            renderCell: item => {
                                if (item.Model.ItemDate == null) {
                                    return (<div />);
                                }
                                else {
                                    const date = moment(item.Model.ItemDate).format("YYYY/MM/DD");
                                    const time = moment(item.Model.ItemDate).format("HH:mm:ss A");
                                    const itemDate = date + " " + cacheStore.GetString("AtLabel") + " " + time;
                                    return (<div>{itemDate}</div>);
                                }
                            }
                        };

                    case "SentFaxRecipient":
                        return {
                            renderCell: item => (
                                <div>{item.Model.RecipientName} ({cacheStore.GetString("FaxDLabel")} {FaxHelper.displayFaxNumberFormat(item.Model.RecipientFax)}) </div>
                            )
                        };

                    case "SentFaxDateSent":
                        return {
                            renderCell: item => {
                                const date = moment(item.Model.DateSent).format("YYYY/MM/DD");
                                const time = moment(item.Model.DateSent).format("HH:mm:ss A");
                                const dateSent = date + " " + cacheStore.GetString("AtLabel") + " " + time;
                                return (<div>{dateSent}</div>);
                            }
                        };

                    case "SentFaxReceivedDate":
                        return {
                            renderCell: item => {
                                if (item.Model.ReceivedDate != undefined) {
                                    const date = moment(item.Model.ReceivedDate).format("YYYY/MM/DD");
                                    const time = moment(item.Model.ReceivedDate).format("HH:mm:ss A");
                                    const receivedDate = date + " " + cacheStore.GetString("AtLabel") + " " + time;
                                    return (<div>{receivedDate}</div>);
                                }
                                else {
                                    return (<div />);
                                }
                            }
                        };

                    case "SentFaxSentBy":
                        return {
                            renderCell: item => (
                                <div>{item.Model.SentBy}</div>
                            )
                        };

                    case "SentFaxStatus":
                        return {
                            renderCell: item => (
                                <PortfolioFaxInboxStatusField faxInboxItem={item} />
                            )
                        };

                    case "SentFaxAction":
                        return {
                            renderCell: item => {
                                const isFeatureFlagEnabled = cacheStore.ClinicSettings.EnableResendFaxForAllStatuses;
                                const isFailedFaxInbox = this.props.inboxType === PortfolioInboxes.FailedFaxes;

                                return isFeatureFlagEnabled
                                    ? this.renderEnhancedFaxActionLinks(item, isFailedFaxInbox)
                                    : this.renderFaxActionLinks(item, isFailedFaxInbox);
                            }
                        };
                }
            },
            false
        );
    }

    private onPreviewFaxGrid(sentFaxTableItem: MySentFaxTableItem): void {
        this.openFaxPreview(sentFaxTableItem.Model);
    }

    private openFaxPreview(sentFaxItem: SentFaxInboxItem): void {
        const withSignature = this.shouldPreviewWithSignature(sentFaxItem);
        this.mutateState(
            s => {
                s.sentFaxItem = sentFaxItem;
                s.isValidPin = false;
                s.showPreview = true;
                s.previewPDF = null;
                s.withSignature = withSignature;
                if (!FaxHelper.isFailedFax(sentFaxItem.SentStatus))
                    s.patientClinicianModel = this.setClinicianModel(sentFaxItem.PatientId);
            },
            () => {
                sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_OpenFaxPreview, {
                    target: MyleTableTarget.Myle_SentFaxes_Inbox_RowClick,
                    rowData: sentFaxItem,
                    withSignature: withSignature
                });
            }
        );
    }

    private shouldPreviewWithSignature(sentFaxItem: SentFaxInboxItem): boolean {
        return FaxHelper.isPinRequired(sentFaxItem.ItemType) &&
            sentFaxItem.SenderEmployeeId == sessionStore.getCurrentEmployee().ContactFacetId;
    }

    private toggleFailedFaxVisibility(SentFaxItem: SentFaxInboxItem): void {
        sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_ToggleFailedVisibility, { SentFaxItem: SentFaxItem });
    }

    private onPreviewClose(): void {
        this.mutateState(s => {
            s.showPreview = false;
            s.previewFileContent = null;
            s.previewPDF = null;
            s.withSignature = false;
            s.sentFaxItem = null;
        });
    }

    private resendFax(sentFaxId: number): void {
        this.mutateState(
            s => {
                s.showPreview = false;
            },
            () => this.debouncedHandle(() => this.resendFaxes([sentFaxId]))
        );
    }

    private setClinicianModel(patientId: string): IPatientCliniciansModel {
        const model: IPatientCliniciansModel = Immutable.fromJS({
            PatientContactFacetId: patientId
        });
        return model.set("IntervenorsOnFile", [].toImmutable());
    }

    private setSelectedFaxes(faxes: MySentFaxTableItem): void {
        this.mutateState(s => {
            s.allFaxes = this.state.allFaxes.mutate(
                fax => fax.IsSelected = !fax.IsSelected,
                fax => fax.Model.SentFaxId === faxes.Model.SentFaxId
            );
            s.isSelectAllChecked = s.allFaxes.all(fax => fax.IsSelected);
        });
    }

    private setSelectedToAllResendFax(): void {
        const enableResendAll = cacheStore.ClinicSettings.EnableResendFaxForAllStatuses;

        this.mutateState(s => {
            s.isSelectAllChecked = !this.state.isSelectAllChecked;
            s.allFaxes = this.state.allFaxes.mutate(
                fax => fax.IsSelected = !this.state.isSelectAllChecked,
                fax => enableResendAll || FaxHelper.isFailedFax(fax.Model.SentStatus)
            );
        });
    }

    private onResendMultipleFaxClick(): void {
        this.debouncedHandle(() => this.resendFaxes(this.state.allFaxes.filter(x => x.IsSelected).map(x => x.Model.SentFaxId)));
    }

    private resendOrOpenResendPopup(model: SentFaxInboxItem): void {
        if (cacheStore.ClinicSettings.EnableResendFaxForAllStatuses) {
            this.mutateState(s => {
                s.isResendFaxOpen = true;
                s.sentFaxItem = model;
            });
        }
        else {
            this.resendFaxes([model.SentFaxId]);
        }
    }

    private isLocked: boolean = false;
    private resendFaxes(faxesIds: number[]): void {
        if (this.isLocked)
            return;
        this.mutateState(s => s.sendingFax = true);
        this.isLocked = true;
        Client.Postv2(
            ServerOperations.Server.Myle_Faxing_ResendFax,
            { SentFaxIds: faxesIds, WithSignature: true },
            response => {
                this.isLocked = false;
                if (!response.Success && !isArrayNullOrUndefinedOrEmpty(response.Errors))
                    sessionStore.showPopup(response.Errors.map(x => x.Message));
                this.mutateState(
                    s => {
                        s.sendingFax = false;
                        s.withSignature = false;
                    },
                    () => sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_Search_CurrentPageChange, { Page: this.props.model.get("CurrentPage") })
                );
            }
        );
    }

    private async resendFaxToUpdatedNumber(faxId: number, faxNumber: string, description: string, phoneNumber: string, pdfCoverPageParams: IFaxCoverSheetProps): Promise<void> {
        const response = await Client.postAsync(
            ServerOperations.Server.Myle_Faxing_ResendFaxWithChanges,
            {
                SentFaxId: faxId,
                FaxNumber: faxNumber,
                FaxNumberDescription: description,
                RecipientPhoneNumber: phoneNumber,
                PdfCoverPageParams: pdfCoverPageParams
            }
        );
        if (!response.isSuccess && !isArrayNullOrUndefinedOrEmpty(response.errors))
            sessionStore.showPopup(response.errors.map(x => x.Message));

        sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_Search_CurrentPageChange, { Page: this.props.model.get("CurrentPage") });
    }

    private onPatientClick(patientContactId: string): void {
        sessionStore.dispatch(LocalOperations.Misc.RedirectToTab, {
            value: patientContactId,
            target: RedirectTo.Patient,
            currentLocation: RouterHistory.location
        });
    }

    private renderFaxActionLinks(
        item: MySentFaxTableItem,
        isFailedFaxInbox: boolean
    ): JSX.Element {
        const isFailedFax = FaxHelper.isFailedFax(item.Model.SentStatus);
        const ignoreLabel = `${isFailedFax && !!cacheStore.faxAccounts?.any() ? "\u00A0/" : ""} ${cacheStore.GetString("IgnoreLabel")}`;
        const restoreLabel = `${isFailedFax && !!cacheStore.faxAccounts?.any() ? "\u00A0/" : ""} ${cacheStore.GetString("RestoreLabel")}`;

        if (item.Model.SentStatus === EnumSentFaxStatus.InProgress) {
            return <div />;
        }

        if (isFailedFaxInbox) {
            return (
                <div>
                    {isFailedFax && !!cacheStore.faxAccounts?.any() && (
                        <a
                            key={"action_resend_" + item.Model.SentFaxId}
                            onClick={() => this.resendOrOpenResendPopup(item.Model)}
                        >
                            {cacheStore.GetString("ResendLabel")}
                        </a>
                    )}
                    {item.Model.FailCounterVisibility ? (
                        <a
                            key={"action_ignore_" + item.Model.SentFaxId}
                            onClick={() => this.toggleFailedFaxVisibility(item.Model)}
                        >
                            {ignoreLabel}
                        </a>
                    ) : (
                        <a
                            key={"action_restore_" + item.Model.SentFaxId}
                            onClick={() => this.toggleFailedFaxVisibility(item.Model)}
                        >
                            {restoreLabel}
                        </a>
                    )
                    }
                </div>
            );
        }

        const showResendAction = (isFailedFax || item.Model.SentStatus === EnumSentFaxStatus.Queued)
            && !!cacheStore.faxAccounts?.any();

        return (
            <div>
                {showResendAction && (
                    <a
                        key={"action_resend_" + item.Model.SentFaxId}
                        onClick={() => this.resendOrOpenResendPopup(item.Model)}
                    >
                        {cacheStore.GetString("ResendLabel")}
                    </a>
                )}
            </div>
        );
    }

    private renderEnhancedFaxActionLinks(item: MySentFaxTableItem, isFailedFaxInbox: boolean): JSX.Element {
        return (
            <div>
                {!!cacheStore.faxAccounts?.any() && (
                    <LinkButton
                        label={cacheStore.GetString("ResendLabel")}
                        onClick={() => this.resendOrOpenResendPopup(item.Model)}
                    />
                )}
                {isFailedFaxInbox && (
                    <span className="marginleft20">
                        <LinkButton
                            label={item.Model.FailCounterVisibility ? cacheStore.GetString("IgnoreLabel") : cacheStore.GetString("RestoreLabel")}
                            onClick={() => this.toggleFailedFaxVisibility(item.Model)}
                        />
                    </span>
                )}
            </div>
        );
    }
}

export { PortfolioFaxInbox };
